import { Document, documentsAPI } from '../../lib/api';
import type { DocumentVersionEntry } from '../../lib/api/documents';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Download, Calendar, User, Loader2, Upload, History } from 'lucide-react';
import { toast } from 'sonner';

interface DocumentVersionsTabProps {
  document: Document;
  versions: DocumentVersionEntry[];
  loading: boolean;
  error: string | null;
  /** Date of the version on screen now (falls back to uploaded_at for pre-tracking rows). */
  currentVersionDate: string | undefined;
  /** Downloads the current file (the page's own download handler). */
  onDownloadCurrent: (doc: Document) => void;
  /** Opens the new-version dialog. Omit to hide the "New version" button. */
  onNewVersion?: () => void;
}

/** Versions tab body of DocumentDetailModal (CR-5328): the current version, then every earlier one. */
export default function DocumentVersionsTab({
  document,
  versions,
  loading,
  error,
  currentVersionDate,
  onDownloadCurrent,
  onNewVersion,
}: DocumentVersionsTabProps) {
  const { formatDate } = useFormatters();

  const handleVersionDownload = async (versionId: number) => {
    try {
      const res = await documentsAPI.downloadVersion(document.id, versionId);
      if (res.data.file_url) {
        window.open(res.data.file_url, '_blank');
      } else {
        toast.error('No file attached to this version.');
      }
    } catch (err) {
      console.error('Error downloading version:', err);
      toast.error('Failed to download this version');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Every version is listed with the person who uploaded it, the date they uploaded it, and their notes. The current version is at the top; uploading a new one moves the current file into the history below.
        </p>
        {onNewVersion && (
          <Button size="sm" onClick={onNewVersion}>
            <Upload className="h-4 w-4 mr-1.5" />
            New version
          </Button>
        )}
      </div>

      {/* Current version row — always at the top, marked Current */}
      <div className="border rounded-md p-3 bg-primary/5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Badge className="text-[10px] py-0 h-4">Current</Badge>
              <span className="text-sm font-medium">
                {document.version || '(no version label)'}
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {document.version_uploaded_by_name
                  || document.uploaded_by_name
                  || '—'}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {currentVersionDate ? formatDate(currentVersionDate) : '—'}
              </span>
              {document.formatted_file_size && (
                <span>{document.formatted_file_size}</span>
              )}
            </div>
            {document.version_notes && (
              <p className="text-xs text-muted-foreground mt-1.5 italic">
                "{document.version_notes}"
              </p>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => onDownloadCurrent(document)}>
            <Download className="h-4 w-4 mr-1.5" />
            Download
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading versions...
        </div>
      ) : error ? (
        <div className="text-center py-6 text-muted-foreground">{error}</div>
      ) : versions.length === 0 ? (
        <div className="text-center py-6 text-muted-foreground text-sm">
          No previous versions.
        </div>
      ) : (
        versions.map(v => (
          <div key={v.id} className="border rounded-md p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <History className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {v.version_number || '(no version label)'}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {v.uploaded_by_name || 'Unknown'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(v.uploaded_at)}
                  </span>
                  {v.formatted_file_size && <span>{v.formatted_file_size}</span>}
                </div>
                {v.notes && (
                  <p className="text-xs text-muted-foreground mt-1.5 italic">
                    "{v.notes}"
                  </p>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => handleVersionDownload(v.id)}>
                <Download className="h-4 w-4 mr-1.5" />
                Download
              </Button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
